import React, { useState, useEffect, useRef } from 'react';

// Grounded Human Bipedal Walking Kinematics (Physical Stance + Swing Phases)
function getHumanLegGait(phase) {
  const t = ((phase % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
  let thigh, knee, foot;

  if (t < Math.PI) {
    // STANCE PHASE (0 -> PI): Foot is planted on ground, pushing backward
    const p = t / Math.PI;
    thigh = 24 - p * 46;
    knee = Math.sin(p * Math.PI) * 7;
    if (p < 0.22) {
      foot = 14 * (1 - p / 0.22);
    } else if (p < 0.72) {
      foot = 0;
    } else {
      foot = -22 * ((p - 0.72) / 0.28);
    }
  } else {
    // SWING PHASE (PI -> 2*PI): Foot lifts, knee flexes, accelerates forward
    const p = (t - Math.PI) / Math.PI;
    thigh = -22 + p * 46;
    knee = Math.sin(p * Math.PI) * 46;
    foot = -14 * (1 - p) + Math.sin(p * Math.PI) * 8;
  }

  return { thigh, knee, foot };
}

// Arm Counter-Swing Gait
function getHumanArmGait(phase) {
  const t = ((phase % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
  const arm = Math.sin(t + Math.PI) * 22;
  const elbow = 10 + Math.max(0, Math.sin(t + Math.PI)) * 26;
  return { arm, elbow };
}

export default function FooterWalker() {
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  // Responsive device check: Track window width
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Constant physical walking speed across all screen sizes (120 pixels/second)
  const PIXEL_SPEED = 120;

  const [characters, setCharacters] = useState([
    {
      id: 'minion',
      name: 'Minion',
      type: 'minion',
      order: 0,
      direction: 1, // walking right
      freq: 5.4,
      height: 82,
      width: 70,
      groundY: 78,
    },
    {
      id: 'shinchan',
      name: 'Shinchan',
      type: 'shinchan',
      order: 1,
      direction: -1, // walking left
      freq: 5.0,
      height: 84,
      width: 62,
      groundY: 77,
    },
    {
      id: 'pikachu',
      name: 'Pikachu',
      type: 'pikachu',
      order: 2,
      direction: 1, // walking right
      freq: 6.8,
      height: 80,
      width: 66,
      groundY: 74,
    },
    {
      id: 'oggy',
      name: 'Oggy & Cockroach',
      type: 'oggy',
      order: 3,
      direction: -1, // walking left
      freq: 5.2,
      height: 76,
      width: 72,
      groundY: 72,
    },
    {
      id: 'jerry',
      name: 'Jerry the Mouse',
      type: 'jerry',
      order: 4,
      direction: 1, // walking right
      freq: 5.0,
      height: 84,
      width: 84,
      groundY: 76,
    },
    {
      id: 'sonic',
      name: 'Sonic the Hedgehog',
      type: 'sonic',
      order: 5,
      direction: -1, // walking left
      freq: 6.5,
      height: 96,
      width: 72,
      groundY: 90,
    },
  ]);

  // Desktop concurrent positions (for screens >= 768px)
  const [desktopPositions, setDesktopPositions] = useState([
    { id: 'minion', x: 6, direction: 1 },
    { id: 'shinchan', x: 22, direction: -1 },
    { id: 'pikachu', x: 38, direction: 1 },
    { id: 'oggy', x: 54, direction: -1 },
    { id: 'jerry', x: 72, direction: 1 },
    { id: 'sonic', x: 88, direction: -1 },
  ]);

  const [globalProgress, setGlobalProgress] = useState(0);
  const [characterStates, setCharacterStates] = useState([]);
  const animFrameRef = useRef(null);
  const lastTimeRef = useRef(performance.now());

  // Mobile sequential relay settings (< 768px: exactly 2 characters in frame)
  const SLOT_TRAVEL = 116; // from -8% to 108%
  const SLOT_SPACING = 58; // 58% spacing ensures 2 characters in frame simultaneously
  const TOTAL_LOOP = SLOT_SPACING * 6; // 348 total loop units

  // 60fps Responsive Continuous Parade Loop
  useEffect(() => {
    const updatePhysics = (currentTime) => {
      const delta = Math.min((currentTime - lastTimeRef.current) / 1000, 0.08);
      lastTimeRef.current = currentTime;

      const isSmallScreen = windowWidth < 900;
      const safeWidth = Math.max(windowWidth, 320);
      const percentPerSecond = (PIXEL_SPEED / safeWidth) * 100;

      if (isSmallScreen) {
        // MOBILE (< 768px): 2 Characters in Frame Relay Loop
        setGlobalProgress((prev) => {
          const nextProgress = (prev + percentPerSecond * delta) % TOTAL_LOOP;

          const computed = characters.map((char) => {
            const charOffset = (nextProgress - char.order * SLOT_SPACING + TOTAL_LOOP) % TOTAL_LOOP;
            const isVisible = charOffset <= SLOT_TRAVEL;

            let x = -999;
            if (isVisible) {
              if (char.direction === 1) {
                // Left to Right: -8% -> 108%
                x = -8 + (charOffset / SLOT_TRAVEL) * 116;
              } else {
                // Right to Left: 108% -> -8%
                x = 108 - (charOffset / SLOT_TRAVEL) * 116;
              }
            }

            const phase = (currentTime / 1000) * char.freq;

            return {
              ...char,
              x,
              phase,
              isVisible,
            };
          });

          setCharacterStates(computed);
          return nextProgress;
        });
      } else {
        // DESKTOP (>= 768px): All 6 Characters Walking Simultaneously across full footer
        setDesktopPositions((prevPositions) => {
          const maxWrapRight = 106;
          const minWrapLeft = -6;

          const nextPositions = prevPositions.map((pos) => {
            let newX = pos.x + pos.direction * percentPerSecond * delta;
            if (pos.direction === 1 && newX > maxWrapRight) {
              newX = minWrapLeft;
            } else if (pos.direction === -1 && newX < minWrapLeft) {
              newX = maxWrapRight;
            }
            return {
              ...pos,
              x: newX,
            };
          });

          const computed = characters.map((char) => {
            const pos = nextPositions.find((p) => p.id === char.id) || { x: 50 };
            const phase = (currentTime / 1000) * char.freq;

            return {
              ...char,
              x: pos.x,
              phase,
              isVisible: true,
            };
          });

          setCharacterStates(computed);
          return nextPositions;
        });
      }

      animFrameRef.current = requestAnimationFrame(updatePhysics);
    };

    animFrameRef.current = requestAnimationFrame(updatePhysics);
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [windowWidth, characters, TOTAL_LOOP, SLOT_SPACING, SLOT_TRAVEL]);

  return (
    <div
      className="absolute top-0 left-0 w-full pointer-events-none z-30 select-none bg-transparent overflow-x-clip"
      style={{ backgroundColor: 'transparent', overflowX: 'clip', overflowY: 'visible' }}
    >
      {/* Render Characters in Sequential Line Parade (One at a time in alternating sequence) */}
      {(characterStates.length > 0 ? characterStates : characters)
        .filter((char) => char.isVisible !== false && char.x > -100)
        .map((char) => {
          const p = char.phase || 0;

          // Grounded Two-Leg Kinematics
          const legL = getHumanLegGait(p);
          const legR = getHumanLegGait(p + Math.PI);
          const armL = getHumanArmGait(p);
          const armR = getHumanArmGait(p + Math.PI);

          // Grounded Vertical Pelvis Bobbing
          const bodyBobY = (1 - Math.abs(Math.sin(p))) * -3.5;
          const torsoTilt = 3.5 + Math.sin(p * 2) * 1.5;

          // Minion Specific Kinematics (Cute waddle & swinging arms)
          const minionWaddle = Math.sin(p) * 6;
          const minionArmL = Math.sin(p + Math.PI) * 28;
          const minionArmR = Math.sin(p) * 28;
          const minionLegL = Math.sin(p) * 24;
          const minionLegR = Math.sin(p + Math.PI) * 24;

          // Pikachu Specific Kinematics
          const pikaTailRot = Math.sin(p) * 22;
          const pikaEarRot = Math.sin(p * 2) * 10;
          const pikaBobY = Math.abs(Math.sin(p)) * -4;

          // Jerry Specific Kinematics (Natural, smooth cartoon mouse stride)
          const jerryTailRot = Math.sin(p) * 18;
          const jerryHopY = (1 - Math.abs(Math.sin(p))) * -3.0;
          const jerryFeetL = Math.sin(p) * 22;
          const jerryFeetR = Math.sin(p + Math.PI) * 22;
          const jerryArmSwing = Math.sin(p) * 16;

          // Shinchan Specific Kinematics
          const shinWaddleTilt = Math.sin(p) * 5;
          const shinArmL = Math.sin(p + Math.PI) * 32;
          const shinArmR = Math.sin(p) * 32;
          const shinLegL = Math.sin(p) * 26;
          const shinLegR = Math.sin(p + Math.PI) * 26;

          // Oggy Specific Kinematics
          const roachRun = Math.sin(p * 3) * 18;

          // Sonic Specific Kinematics
          const sonicQuillWave = Math.sin(p * 2) * 3;

          // Edge fade: seamlessly fade only when crossing off-screen past the footer edges
          let edgeOpacity = 1;
          if (char.x > 100) {
            edgeOpacity = Math.max(0, Math.min(1, (108 - char.x) / 8));
          } else if (char.x < 0) {
            edgeOpacity = Math.max(0, Math.min(1, (char.x - -8) / 8));
          }

          // Character-specific neon theme color for localized footstep walking trail
          const charThemeColor =
            char.type === 'minion'
              ? '#facc15'
              : char.type === 'pikachu'
                ? '#facc15'
                : char.type === 'jerry'
                  ? '#f59e0b'
                  : char.type === 'shinchan'
                    ? '#ef4444'
                    : char.type === 'oggy'
                      ? '#38bdf8'
                      : '#00f0ff';

          return (
            <div
              key={char.id}
              className="absolute pointer-events-none select-none will-change-transform bg-transparent"
              style={{
                left: `${char.x}%`,
                top: `-${char.groundY}px`,
                width: `${char.width}px`,
                height: `${char.height}px`,
                backgroundColor: 'transparent',
                opacity: edgeOpacity,
                zIndex:
                  char.type === 'minion'
                    ? 38
                    : char.type === 'jerry'
                      ? 37
                      : char.type === 'pikachu'
                        ? 36
                        : char.type === 'shinchan'
                          ? 35
                          : char.type === 'oggy'
                            ? 34
                            : 32,
                transform: 'translateX(-50%)',
              }}
            >
              {/* Direction Container (Static: Faces Right for +1, Left for -1) */}
              <div
                className="bg-transparent relative"
                style={{
                  width: `${char.width}px`,
                  height: `${char.height}px`,
                  transform: char.direction === -1 ? 'scaleX(-1)' : 'none',
                  transformOrigin: '50% 100%',
                  backgroundColor: 'transparent',
                }}
              >
                {/* Walking Effect (Appears ONLY directly beside & behind the character while walking) */}
                <div
                  className="absolute pointer-events-none"
                  style={{
                    left: '-10px',
                    top: `${char.groundY - 8}px`,
                    width: `${char.width + 20}px`,
                    height: '14px',
                    zIndex: 1,
                  }}
                >
                  {/* Trailing Footstep Dust Puff (Directly behind back heel) */}
                  <span
                    className="absolute rounded-full pointer-events-none"
                    style={{
                      left: '8px',
                      top: '4px',
                      width: '8px',
                      height: '4px',
                      backgroundColor: charThemeColor,
                      opacity: 0.35 + Math.sin(p * 2) * 0.25,
                      filter: 'blur(1px)',
                      boxShadow: `0 0 6px ${charThemeColor}`,
                      transform: `scale(${0.7 + Math.abs(Math.sin(p)) * 0.4}) translate(${Math.sin(p * 2) * -3}px, 0)`,
                    }}
                  />

                  {/* Dynamic Footstep Contact Spark (Pulses right under stepping foot at ground line) */}
                  <span
                    className="absolute rounded-full pointer-events-none"
                    style={{
                      left: `${char.width * 0.42 + Math.sin(p) * 7}px`,
                      top: '3px',
                      width: '3.5px',
                      height: '3.5px',
                      backgroundColor: '#ffffff',
                      boxShadow: `0 0 8px ${charThemeColor}, 0 0 3px #ffffff`,
                      opacity: 0.4 + Math.abs(Math.sin(p * 2)) * 0.5,
                      transform: `scale(${0.6 + Math.abs(Math.sin(p * 2)) * 0.6})`,
                    }}
                  />
                </div>
                {/* ========================================================================= */}
                {/* 1. ICONIC MINION (RIGHT-FACING PROFILE, GOGGLES, BANANA & DENIM OVERALLS) */}
                {/* ========================================================================= */}
                {char.type === 'minion' && (
                  <svg
                    width={char.width}
                    height={char.height}
                    viewBox="0 0 70 82"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="drop-shadow-[0_0_10px_rgba(250,204,21,0.48)] filter bg-transparent"
                    style={{ backgroundColor: 'transparent' }}
                  >
                    {/* Ground Ambient Yellow Shadow */}
                    <ellipse
                      cx="36"
                      cy="78"
                      rx={14 + Math.sin(p * 2) * 2.5}
                      ry="2.5"
                      fill="rgba(234, 179, 8, 0.35)"
                      className="blur-[2px]"
                    />

                    <g transform={`translate(0, ${bodyBobY}) rotate(${minionWaddle} 35 55)`}>
                      {/* SPROUT HAIR (Curving forward to the right) */}
                      <path d="M32 10 Q30 3 33 2" stroke="#18181b" strokeWidth="1.5" strokeLinecap="round" />
                      <path d="M35 10 Q36 2 40 2" stroke="#18181b" strokeWidth="1.6" strokeLinecap="round" />
                      <path d="M38 10 Q42 3 45 4" stroke="#18181b" strokeWidth="1.5" strokeLinecap="round" />

                      {/* 1. BACK ARM (Left yellow arm swinging back with black glove) */}
                      <g transform={`translate(21, 41) rotate(${minionArmL})`}>
                        <rect x="-3" y="0" width="6" height="12" rx="3" fill="url(#minionYellowGrad)" stroke="#ca8a04" strokeWidth="0.6" />
                        <g transform="translate(0, 10)">
                          <circle cx="0" cy="3.5" r="3.6" fill="#18181b" stroke="#09090b" strokeWidth="0.5" />
                          <ellipse cx="-1.5" cy="4.5" rx="1.2" ry="1.8" fill="#27272a" />
                          <ellipse cx="1.5" cy="4.5" rx="1.2" ry="1.8" fill="#27272a" />
                        </g>
                      </g>

                      {/* 2. BACK LEG (Swinging under overalls) */}
                      <g transform={`translate(25, 60) rotate(${legL.thigh * 0.8})`}>
                        <rect x="-3.5" y="0" width="7" height="12" rx="2" fill="url(#minionDenimGrad)" stroke="#0f172a" strokeWidth="0.6" />
                        <g transform={`translate(0, 10) rotate(${legL.foot * 0.8})`}>
                          <path d="M-5 0 H5 Q8 0 8 3.5 L8 6.5 Q8 8 3 8 H-6 Q-8 8 -8 5 Z" fill="#18181b" stroke="#09090b" strokeWidth="0.7" />
                          <ellipse cx="1" cy="3" rx="3" ry="1.2" fill="#3f3f46" opacity="0.4" />
                        </g>
                      </g>

                      {/* 3. FRONT LEG (Swinging under overalls) */}
                      <g transform={`translate(39, 60) rotate(${legR.thigh * 0.8})`}>
                        <rect x="-3.5" y="0" width="7" height="12" rx="2" fill="url(#minionDenimGrad)" stroke="#0f172a" strokeWidth="0.6" />
                        <g transform={`translate(0, 10) rotate(${legR.foot * 0.8})`}>
                          <path d="M-5 0 H6 Q9 0 9 3.5 L9 6.5 Q9 8 4 8 H-6 Q-8 8 -8 5 Z" fill="#18181b" stroke="#09090b" strokeWidth="0.7" />
                          <ellipse cx="1.5" cy="3" rx="3.5" ry="1.2" fill="#3f3f46" opacity="0.4" />
                        </g>
                      </g>

                      {/* 4. YELLOW PILL CAPSULE BODY */}
                      <rect
                        x="18"
                        y="10"
                        width="36"
                        height="58"
                        rx="18"
                        fill="url(#minionYellowGrad)"
                        stroke="#ca8a04"
                        strokeWidth="1"
                      />

                      {/* 5. GOGGLE STRAP (Black band wrapping around head) */}
                      <rect
                        x="17.5"
                        y="22"
                        width="37"
                        height="6.5"
                        fill="#18181b"
                        stroke="#09090b"
                        strokeWidth="0.6"
                      />

                      {/* 6. METALLIC GOGGLE & EYE (PROMINENT RIGHT-FACING 3/4 PROFILE) */}
                      <g transform="translate(45, 25)">
                        {/* Goggle Frame Cylinder Depth */}
                        <path
                          d="M-6 -10.5 H2 C7 -10.5 10 -6 10 0 C10 6 7 10.5 2 10.5 H-6 Z"
                          fill="url(#minionSilverGrad)"
                          stroke="#334155"
                          strokeWidth="0.8"
                        />

                        {/* Front Silver Goggle Bezel Ring */}
                        <ellipse cx="1.5" cy="0" rx="8.5" ry="10.5" fill="url(#minionSilverGrad)" stroke="#334155" strokeWidth="0.9" />
                        <ellipse cx="1.5" cy="0" rx="6.5" ry="8" fill="#ffffff" stroke="#94a3b8" strokeWidth="0.5" />

                        {/* Metallic Rivet Bracket on Temple */}
                        <rect x="-9.5" y="-3" width="3.5" height="6" rx="1" fill="url(#minionSilverGrad)" stroke="#334155" strokeWidth="0.5" />
                        <circle cx="-7.8" cy="0" r="0.7" fill="#1e293b" />

                        {/* Right-Facing Iris & Pupil */}
                        <ellipse cx="3.5" cy="0" rx="3.3" ry="3.8" fill="#78350f" />
                        <ellipse cx="4.2" cy="0" rx="2" ry="2.4" fill="#09090b" />
                        {/* Specular White Sparkles */}
                        <circle cx="4.8" cy="-1.5" r="1" fill="#ffffff" />
                        <circle cx="3.6" cy="1.5" r="0.4" fill="#ffffff" />

                        {/* Glass Arc Reflection */}
                        <path d="M -2,-4 Q 1.5,-6.5 5,-4" stroke="#ffffff" strokeWidth="0.8" fill="none" opacity="0.8" />
                      </g>

                      {/* 7. CHEERFUL RIGHT-FACING SMILE & TEETH */}
                      <g transform="translate(41, 38)">
                        <path
                          d="M-4 -1 Q2 5 8 -1"
                          stroke="#78350f"
                          strokeWidth="1.3"
                          fill="#18181b"
                          strokeLinecap="round"
                        />
                        {/* Cute Top Teeth */}
                        <path
                          d="M-2 -0.5 Q2 2 6 -0.5"
                          stroke="#ffffff"
                          strokeWidth="1.1"
                          strokeLinecap="round"
                          fill="none"
                        />
                        {/* Rosy Cheek */}
                        <ellipse cx="6.5" cy="-1" rx="2.5" ry="1.5" fill="#f87171" opacity="0.35" />
                      </g>

                      {/* 8. ICONIC BLUE DENIM OVERALLS */}
                      <g>
                        {/* Main Overalls Body (Covering entire lower hemisphere + front bib) */}
                        <path
                          d="M 18,48
                             L 18,50
                             A 18 18 0 0 0 54 50
                             L 54,42
                             L 40,42
                             L 40,48
                             L 18,48
                             Z"
                          fill="url(#minionDenimGrad)"
                          stroke="#0f172a"
                          strokeWidth="0.8"
                        />

                        {/* Contrast Gold Stitching */}
                        <line x1="41" y1="43" x2="53" y2="43" stroke="#f59e0b" strokeWidth="0.4" strokeDasharray="1 0.7" />
                        <path d="M 18,48.5 Q 29,48.5 40,48.5" stroke="#f59e0b" strokeWidth="0.4" strokeDasharray="1 0.8" fill="none" />
                        <path d="M 23,52 Q 28,57 26,62" stroke="#f59e0b" strokeWidth="0.4" strokeDasharray="1 0.8" fill="none" />

                        {/* Front 3/4 Bib Pocket with Gru 'G' Crest */}
                        <path
                          d="M 42,46 H 52 V 51 C 52,54 49.5,55.5 47,55.5 C 44.5,55.5 42,54 42,51 Z"
                          fill="url(#minionPocketGrad)"
                          stroke="#0f172a"
                          strokeWidth="0.6"
                        />
                        <line x1="43" y1="47.2" x2="51" y2="47.2" stroke="#f59e0b" strokeWidth="0.4" strokeDasharray="1 0.7" />
                        <path
                          d="M 43,48 V 51 C 43,53 45,54.2 47,54.2 C 49,54.2 51,53 51,51 V 48"
                          stroke="#f59e0b"
                          strokeWidth="0.35"
                          strokeDasharray="0.8 0.7"
                          fill="none"
                        />

                        {/* Gru Black Medallion & 'G' */}
                        <circle cx="47" cy="51" r="2.4" fill="#18181b" stroke="#09090b" strokeWidth="0.3" />
                        <path
                          d="M 48,50 C 46.8,50 46,50.4 46,51 C 46,51.6 46.8,52 48,52 L 48,51 L 47.3,51"
                          stroke="#ffffff"
                          strokeWidth="0.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          fill="none"
                        />
                      </g>

                      {/* 9. FRONT ARM (Holding Banana pointed forward/right) */}
                      <g transform={`translate(45, 41) rotate(${minionArmR})`}>
                        <rect x="-3" y="0" width="6" height="11" rx="3" fill="url(#minionYellowGrad)" stroke="#ca8a04" strokeWidth="0.6" />
                        <g transform="translate(0, 9)">
                          {/* Black 3-fingered Glove */}
                          <circle cx="0" cy="3.5" r="3.5" fill="#18181b" stroke="#09090b" strokeWidth="0.5" />

                          {/* ICONIC MINION BANANA (Facing Right) */}
                          <g transform={`translate(4, -3) rotate(${15 + Math.sin(p * 2) * 6})`}>
                            {/* Curved Banana Body */}
                            <path
                              d="M -2,9 C -1,3 4,-2 12,-4 C 11,1 7,8 0,11 Z"
                              fill="url(#minionBananaGrad)"
                              stroke="#ca8a04"
                              strokeWidth="0.6"
                            />
                            {/* Banana Brown Tips */}
                            <circle cx="12" cy="-4" r="0.8" fill="#78350f" />
                            <circle cx="-2" cy="9.5" r="0.8" fill="#78350f" />
                            {/* Banana Highlight Stripe */}
                            <path d="M 0,6 C 3,2 6,0 10,-2" stroke="#fef9c3" strokeWidth="0.5" fill="none" />
                          </g>
                        </g>
                      </g>
                    </g>

                    <defs>
                      <linearGradient id="minionYellowGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#fef08a" />
                        <stop offset="50%" stopColor="#facc15" />
                        <stop offset="100%" stopColor="#eab308" />
                      </linearGradient>
                      <linearGradient id="minionDenimGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#38bdf8" />
                        <stop offset="30%" stopColor="#2563eb" />
                        <stop offset="70%" stopColor="#1d4ed8" />
                        <stop offset="100%" stopColor="#0f172a" />
                      </linearGradient>
                      <linearGradient id="minionPocketGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#2563eb" />
                        <stop offset="50%" stopColor="#1d4ed8" />
                        <stop offset="100%" stopColor="#1e3a8a" />
                      </linearGradient>
                      <linearGradient id="minionSilverGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#ffffff" />
                        <stop offset="45%" stopColor="#cbd5e1" />
                        <stop offset="80%" stopColor="#64748b" />
                        <stop offset="100%" stopColor="#334155" />
                      </linearGradient>
                      <linearGradient id="minionBananaGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#fef08a" />
                        <stop offset="60%" stopColor="#facc15" />
                        <stop offset="100%" stopColor="#ca8a04" />
                      </linearGradient>
                    </defs>
                  </svg>
                )}

                {/* ========================================================================= */}
                {/* 2. CUTE ICONIC PIKACHU (LIGHTNING TAIL & ELECTRIC CHEEKS)                */}
                {/* ========================================================================= */}
                {char.type === 'pikachu' && (
                  <svg
                    width={char.width}
                    height={char.height}
                    viewBox="0 0 66 80"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="drop-shadow-[0_0_10px_rgba(250,204,21,0.48)] filter bg-transparent"
                    style={{ backgroundColor: 'transparent' }}
                  >
                    <ellipse
                      cx="33"
                      cy="74"
                      rx={14 + Math.sin(p * 2) * 3}
                      ry="3.5"
                      fill="rgba(250, 204, 21, 0.35)"
                      className="blur-[2px]"
                    />

                    <g transform={`translate(0, ${pikaBobY})`}>
                      {/* ZIGZAG LIGHTNING TAIL */}
                      <g transform={`translate(14, 48) rotate(${pikaTailRot} 0 10)`}>
                        <path
                          d="M0 10 L8 4 L6 -4 L16 -12 L14 -18 L25 -28 L19 -26 L12 -16 L14 -10 L4 -2 L6 6 Z"
                          fill="url(#pikaTailGrad)"
                          stroke="#854d0e"
                          strokeWidth="0.8"
                        />
                      </g>

                      {/* BACK LEG */}
                      <g transform={`translate(24, 58) rotate(${legL.thigh * 0.8})`}>
                        <ellipse cx="0" cy="8" rx="4.5" ry="7" fill="url(#pikaBodyGrad)" stroke="#ca8a04" strokeWidth="0.6" />
                        <ellipse cx="2" cy="14" rx="4" ry="2" fill="#eab308" />
                      </g>

                      {/* CHUBBY BODY */}
                      <ellipse cx="32" cy="48" rx="14" ry="16" fill="url(#pikaBodyGrad)" stroke="#ca8a04" strokeWidth="0.8" />
                      <path d="M22 40 Q25 42 28 40" stroke="#78350f" strokeWidth="2.4" strokeLinecap="round" />
                      <path d="M21 46 Q25 48 29 46" stroke="#78350f" strokeWidth="2.4" strokeLinecap="round" />

                      {/* FRONT LEG */}
                      <g transform={`translate(38, 58) rotate(${legR.thigh * 0.8})`}>
                        <ellipse cx="0" cy="8" rx="4.5" ry="7" fill="url(#pikaBodyGrad)" stroke="#ca8a04" strokeWidth="0.6" />
                        <ellipse cx="2" cy="14" rx="4" ry="2" fill="#eab308" />
                      </g>

                      {/* PIKACHU HEAD */}
                      <g transform="translate(0, 0)">
                        <g transform={`translate(24, 22) rotate(${-28 + pikaEarRot})`}>
                          <path d="M-3.5 0 C-4.5 -12 -1 -24 0 -28 C1 -24 4.5 -12 3.5 0 Z" fill="url(#pikaBodyGrad)" stroke="#ca8a04" strokeWidth="0.7" />
                          <path d="M-2.2 -20 C-1 -25 0 -28 0 -28 C0 -28 1 -25 2.2 -20 Z" fill="#09090b" />
                        </g>

                        <g transform={`translate(38, 20) rotate(${18 + pikaEarRot})`}>
                          <path d="M-3.5 0 C-4.5 -14 -1 -28 0 -32 C1 -28 4.5 -14 3.5 0 Z" fill="url(#pikaBodyGrad)" stroke="#ca8a04" strokeWidth="0.7" />
                          <path d="M-2.2 -22 C-1 -28 0 -32 0 -32 C0 -32 1 -28 2.2 -22 Z" fill="#09090b" />
                        </g>

                        <circle cx="36" cy="27" r="14.5" fill="url(#pikaBodyGrad)" stroke="#ca8a04" strokeWidth="0.8" />
                        <circle cx="45" cy="32" r="4.8" fill="#ef4444" className="drop-shadow-[0_0_6px_#ef4444]" />

                        <circle cx="41" cy="23" r="3.4" fill="#09090b" />
                        <circle cx="42.2" cy="21.8" r="1.3" fill="#ffffff" />
                        <circle cx="40.2" cy="24.2" r="0.6" fill="#ffffff" />

                        <circle cx="48.5" cy="25.5" r="0.9" fill="#09090b" />

                        <path
                          d="M44 29 C46 31 48 31 50 29"
                          stroke="#78350f"
                          strokeWidth="1.2"
                          strokeLinecap="round"
                          fill="none"
                        />
                      </g>

                      {/* FRONT PAWS */}
                      <g transform={`translate(38, 46) rotate(${armR.arm * 0.8})`}>
                        <ellipse cx="2" cy="6" rx="3" ry="5.5" fill="url(#pikaBodyGrad)" stroke="#ca8a04" strokeWidth="0.6" transform="rotate(-15)" />
                      </g>
                    </g>

                    <defs>
                      <linearGradient id="pikaBodyGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#fef08a" />
                        <stop offset="50%" stopColor="#facc15" />
                        <stop offset="100%" stopColor="#eab308" />
                      </linearGradient>
                      <linearGradient id="pikaTailGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#fef08a" />
                        <stop offset="70%" stopColor="#facc15" />
                        <stop offset="100%" stopColor="#854d0e" />
                      </linearGradient>
                    </defs>
                  </svg>
                )}

                {/* ========================================================================= */}
                {/* 3. ICONIC JERRY THE MOUSE (SOLO CARTOON LEGEND WITH SWISS CHEESE)         */}
                {/* ========================================================================= */}
                {char.type === 'jerry' && (
                  <svg
                    width={char.width}
                    height={char.height}
                    viewBox="-14 0 84 84"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="drop-shadow-[0_0_10px_rgba(245,158,11,0.48)] filter bg-transparent overflow-visible"
                    style={{ backgroundColor: 'transparent', overflow: 'visible' }}
                  >
                    {/* Ground Contact Soft Amber Glow Shadow */}
                    <ellipse
                      cx="35"
                      cy="76"
                      rx={14 + Math.sin(p * 2) * 3}
                      ry="3.5"
                      fill="rgba(245, 158, 11, 0.35)"
                      className="blur-[2px]"
                    />

                    <g transform={`translate(0, ${jerryHopY})`}>
                      {/* Jerry's Curling Mouse Tail */}
                      <g transform={`translate(16, 52) rotate(${jerryTailRot} 0 0)`}>
                        <path
                          d="M0 0 C-10 2 -18 -8 -14 -18 C-11 -26 -2 -22 -6 -14 C-10 -7 -2 6 2 2"
                          stroke="#92400e"
                          strokeWidth="2.2"
                          fill="none"
                          strokeLinecap="round"
                        />
                      </g>

                      {/* Jerry's Back Leg */}
                      <g transform={`translate(24, 56) rotate(${jerryFeetL})`}>
                        <ellipse cx="0" cy="6" rx="3.8" ry="8" fill="url(#jerryBodyGrad)" stroke="#78350f" strokeWidth="0.6" />
                        <ellipse cx="2" cy="14" rx="5" ry="2.8" fill="#fed7aa" stroke="#ca8a04" strokeWidth="0.6" />
                      </g>

                      {/* Jerry's Brown Body & Peach Tummy */}
                      <ellipse cx="33" cy="48" rx="13" ry="17" fill="url(#jerryBodyGrad)" stroke="#78350f" strokeWidth="0.8" />
                      <ellipse cx="36" cy="50" rx="8" ry="12" fill="url(#jerryBellyGrad)" />

                      {/* Back Arm (Swinging / balancing) */}
                      <g transform={`translate(26, 42) rotate(${jerryArmSwing})`}>
                        <ellipse cx="-1" cy="5" rx="2.5" ry="5.5" fill="url(#jerryBodyGrad)" stroke="#78350f" strokeWidth="0.6" />
                        <circle cx="-1" cy="11" r="2.8" fill="#fed7aa" />
                      </g>

                      {/* Jerry's Front Leg */}
                      <g transform={`translate(40, 56) rotate(${jerryFeetR})`}>
                        <ellipse cx="0" cy="6" rx="3.8" ry="8" fill="url(#jerryBodyGrad)" stroke="#78350f" strokeWidth="0.6" />
                        <ellipse cx="2" cy="14" rx="5" ry="2.8" fill="#fed7aa" stroke="#ca8a04" strokeWidth="0.6" />
                      </g>

                      {/* Jerry Head & Giant Round Ears */}
                      <g transform="translate(0, 0)">
                        {/* Left Mouse Ear */}
                        <circle cx="25" cy="14" r="9.5" fill="url(#jerryBodyGrad)" stroke="#78350f" strokeWidth="0.8" />
                        <circle cx="25" cy="14" r="6.5" fill="url(#jerryEarPink)" />

                        {/* Right Mouse Ear */}
                        <circle cx="39" cy="11" r="9.5" fill="url(#jerryBodyGrad)" stroke="#78350f" strokeWidth="0.8" />
                        <circle cx="39" cy="11" r="6.5" fill="url(#jerryEarPink)" />

                        {/* Chubby Head Base & Cheeks */}
                        <circle cx="34" cy="27" r="12" fill="url(#jerryBodyGrad)" stroke="#78350f" strokeWidth="0.8" />
                        <ellipse cx="41" cy="30" rx="8" ry="6.5" fill="url(#jerryBellyGrad)" />

                        {/* Large Expressive Cartoon Eye */}
                        <ellipse cx="37" cy="22" rx="3.2" ry="4.8" fill="#ffffff" stroke="#09090b" strokeWidth="0.7" />
                        <ellipse cx="38" cy="22" rx="1.8" ry="3" fill="#09090b" />
                        <circle cx="38.5" cy="20.5" r="0.8" fill="#ffffff" />
                        <circle cx="37.5" cy="23.5" r="0.4" fill="#ffffff" />

                        {/* Cute Black Button Nose */}
                        <ellipse cx="49" cy="26" rx="2" ry="1.5" fill="#09090b" />
                        <circle cx="49.5" cy="25.4" r="0.5" fill="#ffffff" />

                        {/* Cheerful Open Smile & Tongue */}
                        <path d="M40 31 Q45 36 48 31" stroke="#78350f" strokeWidth="1" fill="#be185d" />
                        <ellipse cx="44" cy="33" rx="2.5" ry="1.2" fill="#fb7185" />

                        {/* Mouse Whiskers */}
                        <line x1="46" y1="28" x2="57" y2="26" stroke="#78350f" strokeWidth="0.7" />
                        <line x1="46" y1="30" x2="56" y2="32" stroke="#78350f" strokeWidth="0.7" />

                        {/* Rosy Cheek Blush */}
                        <ellipse cx="36" cy="30" rx="3.5" ry="2.2" fill="#f43f5e" opacity="0.35" />
                      </g>

                      {/* Jerry's Front Arm Holding Golden Swiss Cheese Wedge */}
                      <g transform={`translate(38, 38) rotate(${-jerryArmSwing * 0.5})`}>
                        <ellipse cx="1" cy="4" rx="2.5" ry="5.5" fill="url(#jerryBodyGrad)" stroke="#78350f" strokeWidth="0.6" />
                        <circle cx="2" cy="9" r="2.8" fill="#fed7aa" />

                        {/* Delicious Golden Swiss Cheese */}
                        <g transform="translate(4, 2) rotate(-10)">
                          <polygon points="0,0 15,-4 13,9 0,9" fill="url(#jerryCheeseGrad)" stroke="#ca8a04" strokeWidth="0.8" />
                          <circle cx="4" cy="3" r="1.5" fill="#ea580c" opacity="0.65" />
                          <circle cx="10" cy="1.5" r="1.2" fill="#ea580c" opacity="0.65" />
                          <circle cx="7" cy="6" r="1.4" fill="#ea580c" opacity="0.65" />
                        </g>
                      </g>
                    </g>

                    <defs>
                      <linearGradient id="jerryBodyGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#b45309" />
                        <stop offset="50%" stopColor="#92400e" />
                        <stop offset="100%" stopColor="#78350f" />
                      </linearGradient>
                      <linearGradient id="jerryBellyGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#ffedd5" />
                        <stop offset="50%" stopColor="#fed7aa" />
                        <stop offset="100%" stopColor="#fdba74" />
                      </linearGradient>
                      <linearGradient id="jerryEarPink" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#f472b6" />
                        <stop offset="100%" stopColor="#db2777" />
                      </linearGradient>
                      <linearGradient id="jerryCheeseGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#fef08a" />
                        <stop offset="50%" stopColor="#facc15" />
                        <stop offset="100%" stopColor="#eab308" />
                      </linearGradient>
                    </defs>
                  </svg>
                )}

                {/* ========================================================================= */}
                {/* 4. ICONIC SHINCHAN (CRAYON SHIN-CHAN WADDLE & BIG BROWS)                  */}
                {/* ========================================================================= */}
                {char.type === 'shinchan' && (
                  <svg
                    width={char.width}
                    height={char.height}
                    viewBox="0 0 62 84"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="drop-shadow-[0_0_10px_rgba(239,68,68,0.45)] filter bg-transparent"
                    style={{ backgroundColor: 'transparent' }}
                  >
                    <ellipse
                      cx="31"
                      cy="77"
                      rx={14 + Math.sin(p * 2) * 3}
                      ry="3.5"
                      fill="rgba(239, 68, 68, 0.35)"
                      className="blur-[2px]"
                    />

                    <g transform={`translate(0, ${bodyBobY}) rotate(${shinWaddleTilt} 31 50)`}>
                      {/* BACK ARM */}
                      <g transform={`translate(20, 44) rotate(${shinArmL})`}>
                        <rect x="-3" y="0" width="6.5" height="12" rx="3" fill="url(#shinShirtGrad)" stroke="#b91c1c" strokeWidth="0.6" />
                        <circle cx="0.2" cy="14" r="3.5" fill="url(#shinSkinGrad)" stroke="#c27845" strokeWidth="0.6" />
                      </g>

                      {/* BACK LEG */}
                      <g transform={`translate(24, 60) rotate(${shinLegL})`}>
                        <rect x="-3" y="0" width="6" height="11" rx="2.5" fill="url(#shinSkinGrad)" stroke="#c27845" strokeWidth="0.6" />
                        <rect x="-3.5" y="9" width="7" height="4" rx="1" fill="#ffffff" />
                        <path d="M-5 13 H7 Q9 13 9 17 L-5 17 Z" fill="#eab308" stroke="#854d0e" strokeWidth="0.7" />
                      </g>

                      {/* SHINCHAN RED T-SHIRT & YELLOW SHORTS */}
                      <g transform="translate(0, 0)">
                        <rect x="19" y="38" width="24" height="20" rx="4.5" fill="url(#shinShirtGrad)" stroke="#991b1b" strokeWidth="0.9" />
                        <rect x="21" y="54" width="20" height="12" rx="3" fill="url(#shinShortsGrad)" stroke="#ca8a04" strokeWidth="0.9" />
                      </g>

                      {/* FRONT LEG */}
                      <g transform={`translate(36, 60) rotate(${shinLegR})`}>
                        <rect x="-3" y="0" width="6" height="11" rx="2.5" fill="url(#shinSkinGrad)" stroke="#c27845" strokeWidth="0.6" />
                        <rect x="-3.5" y="9" width="7" height="4" rx="1" fill="#ffffff" />
                        <path d="M-5 13 H7 Q9 13 9 17 L-5 17 Z" fill="#eab308" stroke="#854d0e" strokeWidth="0.7" />
                      </g>

                      {/* SHINCHAN SIGNATURE PEANUT HEAD & BIG EYEBROWS */}
                      <g transform="translate(0, 0)">
                        <path
                          d="M16 28 C16 14 28 10 40 15 C49 19 54 26 53 32 C52 38 44 42 36 42 C24 42 16 38 16 28 Z"
                          fill="url(#shinSkinGrad)"
                          stroke="#c27845"
                          strokeWidth="0.9"
                        />

                        <path
                          d="M17 24 C17 13 30 10 40 15 L39 18 C29 14 19 17 19 25 Z"
                          fill="#09090b"
                        />

                        <path
                          d="M26 17 Q32 12 38 16.5"
                          stroke="#050505"
                          strokeWidth="3.8"
                          strokeLinecap="round"
                          fill="none"
                        />
                        <path
                          d="M40 18.5 Q45 15 49 19.5"
                          stroke="#050505"
                          strokeWidth="3.4"
                          strokeLinecap="round"
                          fill="none"
                        />

                        <ellipse cx="34" cy="24" rx="3.6" ry="4.6" fill="#ffffff" stroke="#09090b" strokeWidth="0.8" />
                        <ellipse cx="35.2" cy="24" rx="2.1" ry="2.9" fill="#09090b" />
                        <circle cx="35.8" cy="23" r="0.7" fill="#ffffff" />

                        <ellipse cx="45" cy="25" rx="3.3" ry="4.3" fill="#ffffff" stroke="#09090b" strokeWidth="0.8" />
                        <ellipse cx="46" cy="25" rx="1.9" ry="2.6" fill="#09090b" />
                        <circle cx="46.5" cy="24" r="0.6" fill="#ffffff" />

                        <path d="M39 33 Q44 37 48 33" stroke="#09090b" strokeWidth="1.2" fill="#be185d" />

                        <ellipse cx="30" cy="32" rx="3.8" ry="2.6" fill="#f87171" opacity="0.45" />
                        <ellipse cx="48" cy="32" rx="3.8" ry="2.6" fill="#f87171" opacity="0.45" />
                      </g>

                      {/* FRONT ARM */}
                      <g transform={`translate(38, 44) rotate(${shinArmR})`}>
                        <rect x="-3" y="0" width="6.5" height="12" rx="3" fill="url(#shinShirtGrad)" stroke="#b91c1c" strokeWidth="0.6" />
                        <circle cx="0.2" cy="14" r="3.5" fill="url(#shinSkinGrad)" stroke="#c27845" strokeWidth="0.6" />
                      </g>
                    </g>

                    <defs>
                      <linearGradient id="shinSkinGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#fff1e6" />
                        <stop offset="60%" stopColor="#fed7aa" />
                        <stop offset="100%" stopColor="#ea580c" />
                      </linearGradient>
                      <linearGradient id="shinShirtGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#ef4444" />
                        <stop offset="60%" stopColor="#dc2626" />
                        <stop offset="100%" stopColor="#991b1b" />
                      </linearGradient>
                      <linearGradient id="shinShortsGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#fef08a" />
                        <stop offset="60%" stopColor="#facc15" />
                        <stop offset="100%" stopColor="#ca8a04" />
                      </linearGradient>
                    </defs>
                  </svg>
                )}

                {/* ========================================================================= */}
                {/* 4. ICONIC OGGY & THE COCKROACH (BLUE CAT & RUNNING JOEY/DEE DEE)          */}
                {/* ========================================================================= */}
                {char.type === 'oggy' && (
                  <svg
                    width={char.width}
                    height={char.height}
                    viewBox="0 0 72 76"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="drop-shadow-[0_0_10px_rgba(56,189,248,0.48)] filter bg-transparent"
                    style={{ backgroundColor: 'transparent' }}
                  >
                    <ellipse cx="36" cy="72" rx={16 + Math.sin(p * 2) * 2.5} ry="2.6" fill="rgba(56, 189, 248, 0.35)" className="blur-[2px]" />

                    {/* LITTLE COCKROACH RUNNING ALONGSIDE OGGY */}
                    <g transform={`translate(${8 + roachRun * 0.3}, 62)`}>
                      <ellipse cx="0" cy="3" rx="4.5" ry="3" fill="#a855f7" stroke="#6b21a8" strokeWidth="0.5" />
                      <circle cx="4" cy="2" r="2" fill="#f43f5e" />
                      <circle cx="4.5" cy="1.5" r="0.8" fill="#ffffff" />
                      <path d="M4 0 Q8 -5 10 -3" stroke="#475569" strokeWidth="0.6" fill="none" />
                      <path d="M3 0 Q6 -6 9 -6" stroke="#475569" strokeWidth="0.6" fill="none" />
                      <line x1="-2" y1="5" x2="-4" y2="9" stroke="#475569" strokeWidth="0.6" />
                      <line x1="0" y1="5" x2="0" y2="9" stroke="#475569" strokeWidth="0.6" />
                      <line x1="2" y1="5" x2="4" y2="9" stroke="#475569" strokeWidth="0.6" />
                    </g>

                    {/* OGGY THE BLUE CAT */}
                    <g transform={`translate(4, ${bodyBobY})`}>
                      {/* Back Arm (Behind body) */}
                      <g transform={`translate(26, 36) rotate(${armL.arm * 0.75})`}>
                        <rect x="-2.5" y="0" width="5" height="11" rx="2.5" fill="#38bdf8" stroke="#0284c7" strokeWidth="0.6" />
                        <g transform="translate(0, 11)">
                          <circle cx="0" cy="2.5" r="3.4" fill="#ffffff" stroke="#0284c7" strokeWidth="0.6" />
                        </g>
                      </g>

                      {/* Back Leg (Behind body - 2-segment articulated walking leg) */}
                      <g transform={`translate(28, 51) rotate(${legL.thigh * 0.85})`}>
                        <rect x="-2.5" y="0" width="5" height="9" rx="2.5" fill="#38bdf8" stroke="#0284c7" strokeWidth="0.6" />
                        <g transform={`translate(0, 7.5) rotate(${-legL.knee * 0.8})`}>
                          <rect x="-2.2" y="0" width="4.4" height="8.5" rx="2" fill="#38bdf8" stroke="#0284c7" strokeWidth="0.6" />
                          <g transform={`translate(0, 7.5) rotate(${legL.foot * 0.75})`}>
                            <path
                              d="M-3.5 0 H3 Q6 0 6.5 2.5 L6.5 4.5 Q6.5 6 2 6 H-4.5 Q-6 6 -6 3.5 Z"
                              fill="#ffffff"
                              stroke="#0284c7"
                              strokeWidth="0.6"
                            />
                            <line x1="1.5" y1="2" x2="1.5" y2="5.5" stroke="#cbd5e1" strokeWidth="0.5" />
                            <line x1="4" y1="2" x2="4" y2="5.5" stroke="#cbd5e1" strokeWidth="0.5" />
                          </g>
                        </g>
                      </g>

                      {/* Oggy Curling Black Cat Tail with White Tip */}
                      <path
                        d="M 23,48 C 13,48 8,34 12,24 C 14,18 20,22 17,28"
                        stroke="#18181b"
                        strokeWidth="2.2"
                        fill="none"
                        strokeLinecap="round"
                      />
                      <circle cx="17" cy="28" r="1.5" fill="#ffffff" />

                      {/* Chubby Pear-Shaped Solid Blue Cat Body */}
                      <ellipse cx="36" cy="44" rx="13" ry="15" fill="#38bdf8" stroke="#0284c7" strokeWidth="0.8" />

                      {/* Front Leg (In front of body - 2-segment articulated walking leg) */}
                      <g transform={`translate(39, 51) rotate(${legR.thigh * 0.85})`}>
                        <rect x="-2.5" y="0" width="5" height="9" rx="2.5" fill="#38bdf8" stroke="#0284c7" strokeWidth="0.6" />
                        <g transform={`translate(0, 7.5) rotate(${-legR.knee * 0.8})`}>
                          <rect x="-2.2" y="0" width="4.4" height="8.5" rx="2" fill="#38bdf8" stroke="#0284c7" strokeWidth="0.6" />
                          <g transform={`translate(0, 7.5) rotate(${legR.foot * 0.75})`}>
                            <path
                              d="M-3.5 0 H3 Q6 0 6.5 2.5 L6.5 4.5 Q6.5 6 2 6 H-4.5 Q-6 6 -6 3.5 Z"
                              fill="#ffffff"
                              stroke="#0284c7"
                              strokeWidth="0.6"
                            />
                            <line x1="1.5" y1="2" x2="1.5" y2="5.5" stroke="#cbd5e1" strokeWidth="0.5" />
                            <line x1="4" y1="2" x2="4" y2="5.5" stroke="#cbd5e1" strokeWidth="0.5" />
                          </g>
                        </g>
                      </g>

                      {/* OGGY HEAD, BIG RED CLOWN NOSE & WHISKERS */}
                      <g transform="translate(0, 0)">
                        {/* Left Ear */}
                        <path d="M26 14 L22 4 L30 10 Z" fill="#38bdf8" stroke="#0284c7" strokeWidth="0.8" />
                        <path d="M25 12 L23 6 L28 10 Z" fill="#f472b6" />

                        {/* Right Ear */}
                        <path d="M38 12 L40 2 L44 9 Z" fill="#38bdf8" stroke="#0284c7" strokeWidth="0.8" />
                        <path d="M39 10 L41 4 L43 8 Z" fill="#f472b6" />

                        {/* Head Circle */}
                        <circle cx="36" cy="22" r="13.5" fill="#38bdf8" stroke="#0284c7" strokeWidth="0.8" />

                        {/* Green Cartoon Eyes */}
                        <ellipse cx="39" cy="16" rx="3.3" ry="5.2" fill="#ffffff" stroke="#09090b" strokeWidth="0.7" />
                        <ellipse cx="40" cy="16" rx="1.8" ry="3.2" fill="#22c55e" />
                        <ellipse cx="40.5" cy="16" rx="1" ry="2" fill="#09090b" />
                        <circle cx="41" cy="14.5" r="0.8" fill="#ffffff" />

                        {/* Big Red Clown Nose */}
                        <circle cx="48" cy="24" r="5.5" fill="#ef4444" stroke="#b91c1c" strokeWidth="0.8" className="drop-shadow-[0_0_8px_#ef4444]" />
                        <circle cx="50" cy="22.5" r="1.8" fill="#ffffff" opacity="0.85" />

                        {/* Smile */}
                        <path d="M42 28 Q46 32 49 28" stroke="#0284c7" strokeWidth="1.2" fill="none" strokeLinecap="round" />

                        {/* Whiskers */}
                        <line x1="45" y1="28" x2="56" y2="28" stroke="#09090b" strokeWidth="0.8" />
                        <line x1="45" y1="31" x2="55" y2="33" stroke="#09090b" strokeWidth="0.8" />
                      </g>

                      {/* Front Arm (In front of body) */}
                      <g transform={`translate(38, 36) rotate(${armR.arm * 0.75})`}>
                        <rect x="-2.5" y="0" width="5" height="11" rx="2.5" fill="#38bdf8" stroke="#0284c7" strokeWidth="0.6" />
                        <g transform="translate(0, 11)">
                          <circle cx="0" cy="2.5" r="3.4" fill="#ffffff" stroke="#0284c7" strokeWidth="0.6" />
                        </g>
                      </g>
                    </g>
                  </svg>
                )}

                {/* ========================================================================= */}
                {/* 5. ICONIC SONIC THE HEDGEHOG (SEGA SPEEDSTER IN POWER SNEAKERS)          */}
                {/* ========================================================================= */}
                {char.type === 'sonic' && (
                  <svg
                    width={char.width}
                    height={char.height}
                    viewBox="0 0 72 96"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="drop-shadow-[0_0_10px_rgba(0,240,255,0.48)] filter bg-transparent"
                    style={{ backgroundColor: 'transparent' }}
                  >
                    <ellipse
                      cx="36"
                      cy="90"
                      rx={15 + Math.sin(p * 2) * 3}
                      ry="3.5"
                      fill="rgba(0, 240, 255, 0.35)"
                      className="blur-[2px]"
                    />

                    <g transform={`translate(0, ${bodyBobY})`}>
                      {/* BACK ARM */}
                      <g transform={`translate(28, 44) rotate(${armL.arm * 1.1})`}>
                        <rect x="-4.5" y="0" width="9" height="13" rx="3.5" fill="#fde68a" stroke="#ca8a04" strokeWidth="0.6" />
                        <g transform={`translate(0, 11) rotate(${armL.elbow})`}>
                          <ellipse cx="0" cy="8" rx="5" ry="2.5" fill="#ffffff" />
                          <circle cx="0" cy="13" r="6" fill="#ffffff" stroke="#cbd5e1" strokeWidth="0.8" />
                        </g>
                      </g>

                      {/* BACK LEG */}
                      <g transform={`translate(28, 58) rotate(${legL.thigh * 1.05})`}>
                        <rect x="-4.5" y="0" width="9" height="13" rx="3.5" fill="#2563eb" stroke="#1d4ed8" strokeWidth="0.6" />
                        <g transform={`translate(0, 12) rotate(${-legL.knee * 0.95})`}>
                          <rect x="-4" y="0" width="8" height="11" rx="3" fill="#2563eb" />
                          <ellipse cx="0" cy="10" rx="5.5" ry="3" fill="#ffffff" stroke="#cbd5e1" strokeWidth="0.6" />
                          <g transform={`translate(0, 9) rotate(${legL.foot})`}>
                            <path
                              d="M-8 0 H10 Q14 0 14 4 L14 7 Q14 9 8 9 H-10 Q-12 9 -12 6 Z"
                              fill="#dc2626"
                              stroke="#991b1b"
                              strokeWidth="1"
                            />
                            <rect x="-1" y="0" width="4" height="9" fill="#ffffff" />
                            <rect x="0" y="2" width="3" height="3.5" fill="#facc15" rx="1" />
                          </g>
                        </g>
                      </g>

                      {/* SONIC BLUE BODY & PEACH BELLY */}
                      <g transform={`rotate(${torsoTilt * 1.1} 36 50)`}>
                        <ellipse cx="36" cy="50" rx="12" ry="14" fill="#2563eb" stroke="#1d4ed8" strokeWidth="1" />
                        <ellipse cx="38" cy="50" rx="8" ry="10" fill="#fde68a" />
                        <path d="M24 45 L14 50 L25 56 Z" fill="#2563eb" stroke="#1d4ed8" strokeWidth="0.8" />
                      </g>

                      {/* FRONT LEG */}
                      <g transform={`translate(44, 58) rotate(${legR.thigh * 1.05})`}>
                        <rect x="-4.5" y="0" width="9" height="13" rx="3.5" fill="#2563eb" stroke="#1d4ed8" strokeWidth="0.6" />
                        <g transform={`translate(0, 12) rotate(${-legR.knee * 0.95})`}>
                          <rect x="-4" y="0" width="8" height="11" rx="3" fill="#2563eb" />
                          <ellipse cx="0" cy="10" rx="5.5" ry="3" fill="#ffffff" stroke="#cbd5e1" strokeWidth="0.6" />
                          <g transform={`translate(0, 9) rotate(${legR.foot})`}>
                            <path
                              d="M-8 0 H10 Q14 0 14 4 L14 7 Q14 9 8 9 H-10 Q-12 9 -12 6 Z"
                              fill="#dc2626"
                              stroke="#991b1b"
                              strokeWidth="1"
                            />
                            <rect x="-1" y="0" width="4" height="9" fill="#ffffff" />
                            <rect x="0" y="2" width="3" height="3.5" fill="#facc15" rx="1" />
                          </g>
                        </g>
                      </g>

                      {/* SONIC SCULPTED HEAD & 3 BLUE QUILLS */}
                      <g transform="translate(0, 0)">
                        <g transform={`rotate(${sonicQuillWave} 34 26)`}>
                          <path d="M30 16 C20 12 8 18 2 24 C14 26 24 24 30 22 Z" fill="#2563eb" stroke="#1d4ed8" strokeWidth="0.9" />
                          <path d="M28 22 C18 22 6 32 0 40 C12 38 22 34 28 30 Z" fill="#2563eb" stroke="#1d4ed8" strokeWidth="0.9" />
                          <path d="M26 30 C16 34 8 46 4 54 C14 48 22 42 26 36 Z" fill="#2563eb" stroke="#1d4ed8" strokeWidth="0.9" />
                        </g>

                        <circle cx="36" cy="24" r="14" fill="#2563eb" stroke="#1d4ed8" strokeWidth="1" />

                        <path d="M26 14 L22 4 L30 10 Z" fill="#2563eb" stroke="#1d4ed8" strokeWidth="0.8" />
                        <path d="M25 12 L23 6 L28 10 Z" fill="#fde68a" />
                        <path d="M36 12 L38 2 L42 9 Z" fill="#2563eb" stroke="#1d4ed8" strokeWidth="0.8" />
                        <path d="M37 10 L39 4 L41 8 Z" fill="#fde68a" />

                        <ellipse cx="44" cy="27" rx="9" ry="8" fill="#fde68a" stroke="#ca8a04" strokeWidth="0.7" />
                        <circle cx="53" cy="24" r="2.2" fill="#09090b" />
                        <circle cx="53.5" cy="23.5" r="0.7" fill="#ffffff" />
                        <path d="M43 31 Q48 34 52 30" stroke="#09090b" strokeWidth="1.2" strokeLinecap="round" fill="none" />

                        <ellipse cx="43" cy="18" rx="4.5" ry="6.5" fill="#ffffff" stroke="#09090b" strokeWidth="0.8" />
                        <ellipse cx="45" cy="18" rx="2.5" ry="4.5" fill="#16a34a" />
                        <ellipse cx="45.5" cy="18" rx="1.5" ry="2.8" fill="#09090b" />
                        <circle cx="46" cy="16" r="0.9" fill="#ffffff" />
                      </g>

                      {/* FRONT ARM */}
                      <g transform={`translate(44, 44) rotate(${armR.arm * 1.1})`}>
                        <rect x="-4.5" y="0" width="9" height="13" rx="3.5" fill="#fde68a" stroke="#ca8a04" strokeWidth="0.6" />
                        <g transform={`translate(0, 11) rotate(${armR.elbow})`}>
                          <ellipse cx="0" cy="8" rx="5" ry="2.5" fill="#ffffff" />
                          <circle cx="0" cy="13" r="6.5" fill="#ffffff" stroke="#cbd5e1" strokeWidth="0.8" />
                        </g>
                      </g>
                    </g>
                  </svg>
                )}
              </div>
            </div>
          );
        })}
    </div>
  );
}
